import type { ColorSource, Graphics } from 'pixi.js';
import { pixelMask, pixelRect, TILE_SYMBOLS } from '@/utils/tilePixels';

const icons = {
  keyboard: [
    '###################',
    '###################',
    '##...............##',
    '##.##.##.##.##.####',
    '##.##.##.##.##.####',
    '##...............##',
    '##.##.##.##.##...##',
    '##.##.##.##.##...##',
    '##...............##',
    '##...#########...##',
    '##...#########...##',
    '###################',
    '###################',
  ],
  mouse: [
    '....#####....',
    '..#########..',
    '.###..#..###.',
    '.##...#...##.',
    '##....#....##',
    '##...###...##',
    '##...###...##',
    '##....#....##',
    '#############',
    '#############',
    '##.........##',
    '##.........##',
    '##.........##',
    '##.........##',
    '.##.......##.',
    '.###.....###.',
    '..#########..',
    '....#####....',
  ],
  sound: [
    '.......##########',
    '.......##########',
    '.......##......##',
    '.......##......##',
    '.......##......##',
    '.......##......##',
    '.......##......##',
    '.......##......##',
    '.......##......##',
    '.......##...#####',
    '.......##..######',
    '.......##..######',
    '....#####...####.',
    '..#######........',
    '.########........',
    '.#######.........',
    '..#####..........',
  ],
  portal: [
    '....#####....',
    '..####.####..',
    '.###.....###.',
    '.##...#...##.',
    '##...###...##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##..##.##..##',
    '##...###...##',
    '.##...#...##.',
    '.###.....###.',
    '..####.####..',
    '....#####....',
  ],
  socket: [
    '..##..##..##...',
    '..##..##..##...',
    '..##..##..##...',
    '###############',
    '###############',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '##...........##',
    '###############',
    '###############',
  ],
  script: [
    '..####.......####..',
    '..####.......####..',
    '..##...........##..',
    '..##......##...##..',
    '..##......##...##..',
    '..##.....##....##..',
    '.###.....##....###.',
    '###......##.....###',
    '.###....##.....###.',
    '..##....##.....##..',
    '..##...##......##..',
    '..##...##......##..',
    '..##...........##..',
    '..####.......####..',
    '..####.......####..',
  ],
  storage: [
    '#################',
    '#################',
    '##..#########..##',
    '##..#########..##',
    '##..##.....##..##',
    '##..##.....##..##',
    '##..#########..##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '##.............##',
    '#################',
    '#################',
  ],
  file: [
    '#############..',
    '##############.',
    '##........#####',
    '##...........##',
    '##...........##',
    '##..#######..##',
    '##..#######..##',
    '##...........##',
    '##...........##',
    '##..#######..##',
    '##..#######..##',
    '##...........##',
    '##...........##',
    '##...........##',
    '###############',
    '###############',
  ],
} as const;

export function drawExtensionIcon(
  g: Graphics,
  icon: keyof typeof icons | 'clock',
  color: ColorSource,
  variant = '',
) {
  const rows = icon === 'clock' ? TILE_SYMBOLS.clock : icons[icon];
  const x = Math.floor((25 - rows[0].length) / 2),
    y = Math.floor((25 - rows.length) / 2);
  pixelMask(g, rows, x, y, color);
  if (['keyboard', 'mouse', 'script', 'storage'].includes(icon))
    pixelMask(g, ['..#..', '.###.', '##.##'], 10, 21, color);
  if (icon === 'portal')
    pixelMask(
      g,
      ['##..', '.##.', '..##', '.##.', '##..'],
      variant === 'send' ? 1 : 20,
      10,
      color,
    );
  if (icon === 'socket') {
    if (variant === 'advance')
      pixelMask(g, ['##...', '.##..', '..##.', '.##..', '##...'], 10, 10, color);
    else if (variant === 'ready') pixelMask(g, TILE_SYMBOLS.bubble, 10, 10, color);
    else
      for (const py of [10, 14])
        for (const px of [8, 12, 16]) pixelRect(g, px, py, 2, 2, color);
  }
  if (icon === 'storage') {
    const arrow = [
      '....##...',
      '.....##..',
      '########.',
      '#########',
      '########.',
      '.....##..',
      '....##...',
    ];
    pixelMask(
      g,
      variant === 'write' ? arrow : arrow.map((row) => [...row].reverse().join('')),
      8,
      12,
      color,
    );
  }
}
